import { useEffect, useMemo, useRef, useState } from "react";
import { useReplicache } from "../replicache/context";
import {
  CANVAS_PREFIX,
  GROUP_PREFIX,
  ITEM_PREFIX,
  NOTE_PREFIX,
  type Canvas,
  type Group,
  type Item,
  type Note,
} from "../replicache/keys";

// ⌘K quick switcher.  Everything lives in the local Replicache store, so
// search is instant and works offline.  One scan per (debounced) keystroke
// keeps it simple — no index to maintain.

export type SearchHit =
  | {
      kind: "item";
      id: string;
      groupId: string;
      title: string;
      context: string;
      done: boolean;
    }
  | { kind: "group"; id: string; title: string }
  | { kind: "note"; id: string; title: string }
  | { kind: "canvas"; id: string; title: string };

const MAX_RESULTS = 25;

export function SearchModal({
  onClose,
  onJump,
}: {
  onClose: () => void;
  onJump: (hit: SearchHit) => void;
}) {
  const rep = useReplicache();
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!rep) return;
    const q = query.trim().toLowerCase();
    if (q.length < 2) {
      setHits([]);
      setActive(0);
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      void rep
        .query(async (tx) => {
          const [groups, items, notes, canvases] = await Promise.all([
            tx.scan<Group>({ prefix: GROUP_PREFIX }).values().toArray(),
            tx.scan<Item>({ prefix: ITEM_PREFIX }).values().toArray(),
            tx.scan<Note>({ prefix: NOTE_PREFIX }).values().toArray(),
            tx.scan<Canvas>({ prefix: CANVAS_PREFIX }).values().toArray(),
          ]);
          const groupName = new Map(groups.map((g) => [g.id, g.name]));
          // Rank: title prefix match first, then substring; completed last.
          const score = (text: string, done = false): number | null => {
            const idx = text.toLowerCase().indexOf(q);
            if (idx < 0) return null;
            return (idx === 0 ? 0 : 1) + (done ? 2 : 0);
          };
          const out: { hit: SearchHit; rank: number }[] = [];
          for (const it of items) {
            const s =
              score(it.title, it.isComplete) ??
              // Notes-field match ranks below any title match.
              (it.notes && score(it.notes) !== null ? 4 : null);
            if (s === null) continue;
            out.push({
              rank: s,
              hit: {
                kind: "item",
                id: it.id,
                groupId: it.groupId,
                title: it.title || "(untitled)",
                context: groupName.get(it.groupId) ?? "",
                done: it.isComplete,
              },
            });
          }
          for (const g of groups) {
            const s = score(g.name);
            if (s === null) continue;
            out.push({ rank: s, hit: { kind: "group", id: g.id, title: g.name } });
          }
          for (const n of notes) {
            const s = score(n.name) ?? (score(n.body) !== null ? 4 : null);
            if (s === null) continue;
            out.push({
              rank: s,
              hit: { kind: "note", id: n.id, title: n.name || "(untitled)" },
            });
          }
          for (const c of canvases) {
            const s = score(c.name);
            if (s === null) continue;
            out.push({
              rank: s,
              hit: { kind: "canvas", id: c.id, title: c.name || "(untitled)" },
            });
          }
          out.sort((a, b) => a.rank - b.rank);
          return out.slice(0, MAX_RESULTS).map((o) => o.hit);
        })
        .then((res) => {
          if (!cancelled) {
            setHits(res);
            setActive(0);
          }
        });
    }, 120);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [rep, query]);

  const jump = (hit: SearchHit) => {
    onClose();
    onJump(hit);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      onClose();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, hits.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter" && hits[active]) {
      e.preventDefault();
      jump(hits[active]);
    }
  };

  // Keep the active row in view while arrowing.
  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-idx="${active}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const kindBadge: Record<SearchHit["kind"], string> = useMemo(
    () => ({ item: "task", group: "group", note: "note", canvas: "canvas" }),
    [],
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 dark:bg-slate-950/70"
      style={{ paddingTop: "max(5rem, env(safe-area-inset-top))" }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal
        aria-label="Search"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
        className="w-full max-w-lg rounded-lg bg-white shadow-xl dark:bg-slate-900"
      >
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search tasks, groups, notes…"
          aria-label="Search"
          className="w-full rounded-t-lg border-b border-slate-200 px-4 py-3 text-sm focus:outline-none dark:border-slate-700"
        />
        {query.trim().length >= 2 && (
          <ul ref={listRef} className="max-h-80 overflow-y-auto p-1">
            {hits.length === 0 && (
              <li className="px-3 py-4 text-center text-sm text-slate-400 dark:text-slate-500">
                No matches.
              </li>
            )}
            {hits.map((hit, i) => (
              <li key={`${hit.kind}:${hit.id}`} data-idx={i}>
                <button
                  type="button"
                  onClick={() => jump(hit)}
                  onMouseMove={() => setActive(i)}
                  className={`flex w-full items-baseline gap-2 rounded-md px-3 py-1.5 text-left text-sm ${
                    i === active ? "bg-slate-100 dark:bg-slate-800" : ""
                  }`}
                >
                  <span className="w-12 shrink-0 text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500">
                    {kindBadge[hit.kind]}
                  </span>
                  <span
                    className={`truncate ${
                      hit.kind === "item" && hit.done
                        ? "text-slate-400 line-through dark:text-slate-500"
                        : "text-slate-800 dark:text-slate-100"
                    }`}
                  >
                    {hit.title}
                  </span>
                  {hit.kind === "item" && hit.context && (
                    <span className="ml-auto shrink-0 pl-2 text-xs text-slate-400 dark:text-slate-500">
                      {hit.context}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="border-t border-slate-100 px-3 py-1.5 text-[10px] text-slate-400 dark:border-slate-800 dark:text-slate-500">
          ↑↓ navigate · Enter open · Esc close
        </div>
      </div>
    </div>
  );
}
